/**
 * ZALT Buy Box — site default (won the June–July 2026 A/B test vs the
 * April PDP; called 2026-07-31 at 91% confidence)
 *
 * Model: Flavor (Mixed 4-Pack OR Founder bundle OR single flavor) →
 * Pack size (3/5 cans, fixed 4 for either bundle) → Plan (one-time /
 * Subscribe & Save). Two fixed-pack bundle tiers since 2026-09-15:
 * 'mix' (raw.mixPack, left tile, the default pick — the Liquid
 * server-renders it active) and 'sampler' (raw.fourPack, right tile).
 * With the introductory offer enabled, the Mixed 4-Pack on Subscribe &
 * Save shows the starter-offer panel under the Plan step (display only —
 * the regular Add to Cart buys it).
 *
 * Plan defaults (deliberate):
 *   - Subscribe & Save pre-selected on every tier (sampler included).
 *     Every fresh page load lands on this default.
 *   - An EXPLICIT plan pick persists across flavor/pack switches while
 *     on the page — the form never silently re-selects subscription
 *     after the visitor chose one-time.
 *
 * Displayed prices are the real Shopify variant / selling-plan-allocation
 * prices from the section's JSON blob — what you see is what /cart/add.js
 * charges. No display overrides.
 */
(function () {
  'use strict';

  /* tint = active card fill (v2 design's explicit per-flavor pastels) */
  var FLAVOR_CONFIG = {
    'Watermelon':          { color: '#E0524C', tint: '#F7DCDA' },
    'Cool Mint':           { color: '#6BCBE3', tint: '#DDF3F8' },
    'Strawberry Lemonade': { color: '#E56D84', tint: '#FBE2E8' },
    'Lemon Lime':          { color: '#9FC93C', tint: '#EFF6D8' },
    /* retired 2026-08 (kept for subscriber contracts / deep links) */
    'Cucumber Citrus':     { color: '#4CAF6E', tint: '#DBF0E2' },
    'Ginger Lime':         { color: '#E8C547', tint: '#FAF0D3' }
  };

  // Canonical display order for the flavor grid + sticky dots. Sold-out
  // flavors are sorted after this (see the sort below); unknown flavors
  // fall to the end of the in-stock group.
  /* Fruit trio first, Cool Mint last: it is the odd one out of the lineup
     (Dom 2026-08-21). Sold-out flavors still sort after this. */
  var FLAVOR_DISPLAY_ORDER = ['Watermelon', 'Lemon Lime', 'Strawberry Lemonade', 'Cool Mint'];

  /* ------------------------------------------------------------------ */
  /* DOM refs                                                            */
  /* ------------------------------------------------------------------ */

  var jsonEl = document.querySelector('.bxb-section [data-product-json]');
  if (!jsonEl) return;

  var samplerBtn    = document.querySelector('[data-pick-sampler]');
  var mixBtn        = document.querySelector('[data-pick-mix]');
  /* Starter-offer content (Dom 2026-09-29): folded into the Subscribe &
     Save plan card when the Mixed 4-Pack is selected — the Liquid renders
     the deliveries strip into this template, renderPlans() injects it. */
  var loyaltyPlanTpl = document.querySelector('[data-loyalty-plan-extra]');
  var loyaltyPlanHtml = loyaltyPlanTpl ? loyaltyPlanTpl.innerHTML : '';
  var flavorGrid    = document.querySelector('[data-flavor-grid]');
  var packSection   = document.querySelector('[data-pack-section]');
  var packStep      = document.querySelector('[data-pack-step]');
  var packHint      = document.querySelector('[data-pack-hint]');
  var planSection   = document.querySelector('[data-plan-section]');
  var buyBtn        = document.querySelector('[data-buy-now]');
  var ctaPriceEl    = document.querySelector('[data-cta-price]');
  var buyErrorEl    = document.querySelector('[data-buy-error]');
  var ctaAnchor     = document.querySelector('[data-cta-anchor]');
  var stickyBar     = document.querySelector('[data-sticky-bar]');
  var stickyConfig  = document.querySelector('[data-sticky-config]');
  var stickyPlan    = document.querySelector('[data-sticky-plan]');
  var stickyPrice   = document.querySelector('[data-sticky-price]');
  var stickyBuy     = document.querySelector('[data-sticky-buy]');
  var stickyDots    = document.querySelector('[data-sticky-dots]');
  var galleryMain   = document.querySelector('.bxb-section [data-gallery-main]');
  var galleryThumbs = document.querySelector('.bxb-section [data-gallery-thumbs]');

  /* ------------------------------------------------------------------ */
  /* Data                                                                */
  /* ------------------------------------------------------------------ */

  var raw = JSON.parse(jsonEl.textContent);
  var fivePack  = raw.fivePack;
  var fourPack  = raw.fourPack || null;
  var mixPack   = raw.mixPack || null;
  var threePack = raw.threePack || null;
  // Standalone "Just Subscribe" page (/pages/instant-subscribe): hide the
  // one-time plan, force Subscribe & Save, relabel the CTA. Off on the PDP arm.
  var subscribeOnly = !!raw.subscribeOnly;
  // Global kill switch (theme setting buybox_one_time_only, Dom 2026-09-15):
  // no Plan step anywhere, every add is a one-time purchase at the reserve
  // discount, even though the v2 products carry Skio plans. subscribeOnly
  // pages win over it so the instant-subscribe winback keeps working.
  var oneTimeOnly = !!raw.oneTimeOnly && !subscribeOnly;
  var ctaText       = raw.ctaLabel || 'Add to Cart';
  /* Bundle-tile copy is editor-controlled; these were hardcoded literals. */
  var SAMPLER_NAME = raw.samplerName || '4-Can Sampler';
  var SAMPLER_SUB  = raw.samplerSub  || 'One of each flavor';
  var MIX_NAME     = raw.mixName || 'Mixed 4 Pack';
  var MIX_SUB      = raw.mixSub  || 'One of each flavor';
  var flavorCards = raw.flavorCards || {};
  var subPerks  = raw.subPerks || [];
  var subDiscountPct = raw.subDiscountPct != null ? raw.subDiscountPct : 30;

  /* Preorder discount DISPLAY (reserve_discount_pct). Display only: the
     actual money comes off via the "V2 Preorder 20% Off" AUTOMATIC discount
     in admin, so what we show here is what checkout charges. The setting
     must match the admin discount or the page lies. 0 = off. */
  var reserveDiscountPct = raw.reserveDiscountPct != null ? raw.reserveDiscountPct : 0;

  function reserveOff(cents) {
    return reserveDiscountPct > 0 ? Math.round(cents * (1 - reserveDiscountPct / 100)) : cents;
  }



  // Flavors staged in Shopify but not launched (no card art / no announce
  // yet) — comma-separated section setting, matched case-insensitively.
  // Filtering here hides them everywhere downstream: grid, sticky dots,
  // URL deep-links (an unmatched ?flavor falls back to the default bundle).
  var hiddenFlavors = (raw.hiddenFlavors || '').split(',').map(function (s) {
    return s.trim().toLowerCase();
  }).filter(Boolean);

  /* Flavors visible but not yet reservable ("In stock soon"): the tile stays
     on the page as a tease but renders disabled with a pill and sorts to the
     end of the grid, exactly like a sold-out flavor. Unlike hiddenFlavors
     the customer still sees it coming. Comma-separated section setting. */
  var comingSoonFlavors = (raw.comingSoonFlavors || '').split(',').map(function (s) {
    return s.trim().toLowerCase();
  }).filter(Boolean);

  function comingSoon(flavor) {
    return comingSoonFlavors.indexOf(flavor.toLowerCase()) !== -1;
  }

  // Flavors come from the 5-pack's variant option1 values.
  var flavors = [];
  fivePack.variants.forEach(function (v) {
    var f = v.options[0];
    if (!f || hiddenFlavors.indexOf(f.toLowerCase()) !== -1) return;
    if (flavors.indexOf(f) === -1) flavors.push(f);
  });

  /* Tier map. byFlavor is null for the sampler (one variant for all). */
  function buildByFlavor(prod) {
    var map = {};
    prod.variants.forEach(function (v) {
      var f = v.options[0];
      if (!f) return;
      map[f] = {
        id: v.id,
        price: v.price,
        available: v.available,
        image: v.image || null,
        imageAlt: v.imageAlt || '',
        allocations: v.sellingPlanAllocations || [],
        productId: prod.id,
        productTitle: prod.title,
        title: prod.title + ' / ' + f
      };
    });
    return map;
  }

  var TIERS = {};
  if (fourPack && fourPack.variants.length > 0) {
    var sv = fourPack.variants[0];
    TIERS.sampler = {
      id: 'sampler', cans: 4, label: '4 cans', sub: SAMPLER_SUB,
      variant: {
        id: sv.id,
        price: sv.price,
        available: sv.available,
        image: sv.image || null,
        imageAlt: sv.imageAlt || '',
        allocations: sv.sellingPlanAllocations || [],
        productId: fourPack.id,
        productTitle: fourPack.title,
        title: fourPack.title
      }
    };
  }
  /* Second fixed-pack bundle: the Mixed 4-Pack, same single-variant shape
     as the sampler. Absent mixPack (product unset or draft) — no tier, no
     tile, and every 'mix' branch below is dead code. */
  if (mixPack && mixPack.variants.length > 0) {
    var mv = mixPack.variants[0];
    TIERS.mix = {
      id: 'mix', cans: 4, label: '4 cans', sub: MIX_SUB,
      variant: {
        id: mv.id,
        price: mv.price,
        available: mv.available,
        image: mv.image || null,
        imageAlt: mv.imageAlt || '',
        allocations: mv.sellingPlanAllocations || [],
        productId: mixPack.id,
        productTitle: mixPack.title,
        title: mixPack.title
      }
    };
  }
  if (threePack && threePack.variants.length > 0) {
    TIERS.c3 = { id: 'c3', cans: 3, label: '3 cans', byFlavor: buildByFlavor(threePack) };
  }
  TIERS.s1 = { id: 's1', cans: 5, label: '5 cans', byFlavor: buildByFlavor(fivePack) };

  /* Picking a single flavor defaults to the 5-can sleeve (Dom 2026-07-06;
     supersedes the 3-can default from 2026-06-14). The 3-can tile stays
     available as a step-down pick. Per-flavor stock is still checked in
     pickFlavor. Subscribe & Save is pre-selected on whichever tier wins
     (effectivePlan). */
  var DEFAULT_SINGLE_TIER = 's1';

  /* Order the flavor grid: canonical order first, sold-out flavors pushed
     to the end (availability is static, so this one-time sort holds).
     flavorAvailable() is a hoisted declaration and reads TIERS, built above. */
  function flavorRank(flavor) {
    var i = FLAVOR_DISPLAY_ORDER.indexOf(flavor);
    return i === -1 ? FLAVOR_DISPLAY_ORDER.length : i;
  }
  flavors.sort(function (a, b) {
    var aOut = !flavorAvailable(a), bOut = !flavorAvailable(b);
    if (aOut !== bOut) return aOut ? 1 : -1;
    return flavorRank(a) - flavorRank(b);
  });

  /* ------------------------------------------------------------------ */
  /* Skio Dynamic Build-a-Box                                            */
  /* ------------------------------------------------------------------ */

  var skioBoxProductId = raw.skioDynamicBoxProductId || '';
  var skioBoxIdPromise = null;
  // Skip the lookup when subscriptions can't be picked at all.
  if (skioBoxProductId && !oneTimeOnly) {
    skioBoxIdPromise = fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productPlatformId: String(skioBoxProductId) })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var box = json && (json.DynamicBox || json.dynamicBox);
        if (box && box.boxId) return box.boxId;
        console.warn('Skio: get-dynamic-box returned no boxId', json);
        return null;
      })
      .catch(function (err) {
        console.warn('Skio: get-dynamic-box failed', err);
        return null;
      });
  }

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */

  /* planChoice holds an explicit pick only — it survives every flavor/pack
     switch on the page (no silent re-select mid-configuration, 006 §2.5)
     but deliberately NOT page loads: every fresh load lands on the
     Subscribe & Save default. */
  /* The mix bundle is the default pick (Dom 2026-09-15); the Liquid
     server-renders its tile active and its price/hero to match. Without a
     mix product the founder sampler takes the default back. */
  var DEFAULT_BUNDLE = TIERS.mix ? 'mix' : 'sampler';
  var state = {
    flavor: DEFAULT_BUNDLE, // 'sampler' | 'mix' | flavor name
    tier: DEFAULT_BUNDLE,   // 'sampler' | 'mix' | 'c3' | 's1'
    planChoice: null
  };

  var cartPending = false;

  function isSampler() { return state.tier === 'sampler'; }
  /* Either fixed-pack bundle: everywhere the pack ladder / hint / flavor
     grid logic used to ask isSampler(), the mix behaves identically. */
  function isBundle() { return state.tier === 'sampler' || state.tier === 'mix'; }

  function currentVariant() {
    var t = TIERS[state.tier];
    if (!t) return null;
    if (t.variant) return t.variant;
    return t.byFlavor[state.flavor] || null;
  }

  /* Effective plan: Subscribe & Save pre-selected on every tier (matches
     the control arm's default — keeps the plan default constant across the
     A/B so the test reads layout, not defaults). An explicit choice always
     wins; no allocation → one-time. */
  function effectivePlan() {
    var v = currentVariant();
    var canSub = !!(v && v.allocations.length > 0);
    if (!canSub) return 'one-time';
    if (subscribeOnly) return 'subscribe';
    if (oneTimeOnly) return 'one-time';
    if (state.planChoice) return state.planChoice;
    return 'subscribe';
  }

  /* Subscribe display price. Skio selling plans are configured at 0% —
     the real 30% is a cart-level automatic discount (006 §3.2) — so
     allocation prices come back at FULL price. Display the configured
     discount off the one-time price (must match the SUBSCRIPTION DISCOUNT
     in Shopify admin); if Skio ever carries a real allocation discount,
     the lower allocation price wins. Returns null when not subscribable. */
  function subPriceCents(v) {
    if (!v || v.allocations.length === 0) return null;
    var allocPrice = v.allocations[0].price;
    if (allocPrice < v.price) return allocPrice;
    return Math.round(v.price * (1 - subDiscountPct / 100));
  }

  function currentPriceCents() {
    var v = currentVariant();
    if (!v) return 0;
    if (effectivePlan() === 'subscribe') {
      var sp = subPriceCents(v);
      if (sp != null) return sp;
    }
    // One-time price net of the preorder automatic discount — matches what
    // cart and checkout actually charge.
    return reserveOff(v.price);
  }

  function configLabel() {
    if (isSampler()) return SAMPLER_NAME;
    if (state.tier === 'mix') return MIX_NAME;
    return state.flavor + ' · ' + TIERS[state.tier].label;
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function fmt(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  var CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"></path></svg>';

  /* A flavor is buyable if ANY single-flavor tier (3-can or 5-can) has it
     in stock — checked per-tier so a 0-stock default tier doesn't flag the
     whole flavor sold-out. */
  function flavorAvailable(flavor) {
    if (comingSoon(flavor)) return false; // teased, not yet reservable
    var c3 = TIERS.c3 && TIERS.c3.byFlavor[flavor];
    var s1 = TIERS.s1 && TIERS.s1.byFlavor[flavor];
    return !!((c3 && c3.available) || (s1 && s1.available));
  }

  /* ------------------------------------------------------------------ */
  /* Renderers                                                           */
  /* ------------------------------------------------------------------ */

  /* Built ONCE at init; selection only toggles classes afterwards.
     Rebuilding innerHTML on every pick recreates the <img> nodes and makes
     all four card images flash. */
  function buildFlavorGrid() {
    var html = '';
    /* Mix tile folded in as the grid's first card (Dom 2026-09-29): the
       default pick, drawn with the Month-1 stacked-cans image. */
    if (TIERS.mix && TIERS.mix.variant) {
      var mixSoldOut = !TIERS.mix.variant.available;
      html += '<div class="bxb__fcol">';
      html += '  <button type="button" class="bxb__fcard' + (mixSoldOut ? ' is-soldout' : '') + '"'
            + ' style="--fc:#CC5951;--fc-tint:#F7DCDA;" data-flavor="mix"'
            + (mixSoldOut ? ' aria-disabled="true"' : '')
            + ' aria-label="' + escapeHtml(MIX_NAME) + (mixSoldOut ? ' — sold out' : '') + '">';
      html += '    <span class="bxb__radio bxb__fradio" aria-hidden="true"></span>';
      if (raw.mixCardImage) {
        html += '    <img src="' + raw.mixCardImage + '" alt="" width="400" height="400" decoding="async">';
      } else {
        html += '    <span class="bxb__fdot" style="background:#CC5951;"></span>';
      }
      if (mixSoldOut) html += '<span class="bxb__fsold">Sold out</span>';
      html += '  </button>';
      html += '  <span class="bxb__fname">' + escapeHtml(MIX_NAME) + '</span>';
      html += '</div>';
    }
    flavors.forEach(function (flavor) {
      var cfg = FLAVOR_CONFIG[flavor] || { color: '#ccc' };
      var soon = comingSoon(flavor);
      var soldOut = !soon && !flavorAvailable(flavor);
      var img = flavorCards[flavor];
      html += '<div class="bxb__fcol">';
      html += '  <button type="button" class="bxb__fcard' + (soldOut ? ' is-soldout' : '') + (soon ? ' is-soon' : '') + '"'
            + ' style="--fc:' + cfg.color + ';' + (cfg.tint ? '--fc-tint:' + cfg.tint + ';' : '') + '" data-flavor="' + escapeHtml(flavor) + '"'
            + (soldOut || soon ? ' aria-disabled="true"' : '')
            + ' aria-label="' + escapeHtml(flavor) + (soldOut ? ' — sold out' : (soon ? ' — in stock soon' : '')) + '">';
      html += '    <span class="bxb__radio bxb__fradio" aria-hidden="true"></span>';
      if (img) {
        /* width/height are the real intrinsic size of the flavor renders
           (emitted at image_url: width: 400, sources are 400x400). The
           card reserves the box via aspect-ratio, so these only satisfy
           the "explicit width and height" audit. */
        /* Descriptive alt for image search ("ZALT Watermelon electrolyte
           pouches"). The card button already carries an aria-label with the
           flavour name, so this is SEO rather than a11y. SEO audit 2026-10,
           task 9. */
        html += '    <img src="' + img + '" alt="ZALT ' + escapeHtml(flavor) + ' electrolyte pouches" width="400" height="400" decoding="async">';
      } else {
        html += '    <span class="bxb__fdot" style="background:' + cfg.color + ';"></span>';
      }
      if (soldOut) html += '<span class="bxb__fsold">Sold out</span>';
      if (soon) html += '<span class="bxb__fsold bxb__fsoon">In stock soon</span>';
      html += '  </button>';
      html += '  <span class="bxb__fname">' + escapeHtml(flavor) + '</span>';
      html += '</div>';
    });
    flavorGrid.innerHTML = html;

    flavorGrid.querySelectorAll('[data-flavor]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var flavor = btn.getAttribute('data-flavor');
        if (flavor === 'mix') {
          if (TIERS.mix && TIERS.mix.variant && TIERS.mix.variant.available) pickFlavor('mix');
          return;
        }
        if (!flavorAvailable(flavor)) return;
        pickFlavor(flavor);
      });
    });
  }

  function renderFlavors() {
    flavorGrid.querySelectorAll('[data-flavor]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-flavor') === state.flavor);
    });
    /* Either tile can be absent now: the mix when its product is unset,
       the sampler when the founder kill switch (buybox_show_founder) is
       off — hence the null guards. */
    if (samplerBtn) samplerBtn.classList.toggle('is-active', state.flavor === 'sampler');
    if (mixBtn) mixBtn.classList.toggle('is-active', state.flavor === 'mix');
  }

  function renderPacks() {
    /* Bundles fix the pack at 4 cans, and a one-card radio is not a choice:
       the whole step disappears (Dom 2026-10-01). The Liquid ships both
       nodes hidden because the box opens on the mix. */
    if (isBundle()) {
      packHint.style.display = 'none';
      if (packStep) packStep.style.display = 'none';
      packSection.style.display = 'none';
      packSection.innerHTML = '';
      return;
    }

    if (packStep) packStep.style.display = '';
    packSection.style.display = '';
    packHint.style.display = '';

    var v5 = TIERS.s1.byFlavor[state.flavor];
    var v3 = TIERS.c3 ? TIERS.c3.byFlavor[state.flavor] : null;

    // Only show the 3/5 ladder when both tiers are buyable for this flavor.
    if (!v3 || !v3.available) {
      state.tier = 's1';
      packSection.innerHTML =
        '<div class="bxb__pack is-active is-fixed bxb__pack--stacked">' +
        '  <span class="bxb__pack-top">' +
        '    <span class="bxb__radio"></span>' +
        '    <span class="bxb__pack-name">5 cans</span>' +
        '  </span>' +
        '  <span class="bxb__pack-sub">5 of the same flavor</span>' +
        '</div>';
      return;
    }

    // Same card as the fixed "4 cans" row (radio + name, subtext below),
    // two-up in a row split.
    packSection.innerHTML =
      '<div class="bxb__packrow">' +
      '<button type="button" class="bxb__pack bxb__pack--stacked' + (state.tier === 'c3' ? ' is-active' : '') + '" data-tier="c3">' +
      '  <span class="bxb__pack-top">' +
      '    <span class="bxb__radio"></span>' +
      '    <span class="bxb__pack-name">3 cans</span>' +
      '  </span>' +
      '  <span class="bxb__pack-sub">3 of the same flavor</span>' +
      '</button>' +
      '<button type="button" class="bxb__pack bxb__pack--stacked' + (state.tier === 's1' ? ' is-active' : '') + '" data-tier="s1">' +
      '  <span class="bxb__pack-top">' +
      '    <span class="bxb__radio"></span>' +
      '    <span class="bxb__pack-name">5 cans</span>' +
      '  </span>' +
      '  <span class="bxb__pack-sub">5 of the same flavor</span>' +
      '</button>' +
      '</div>';

    packSection.querySelectorAll('[data-tier]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.tier = btn.getAttribute('data-tier');
        renderAll();
        fireViewItem();
      });
    });
  }

  function starterOfferAvailable() {
    var v = TIERS.mix && TIERS.mix.variant;
    return !!(raw.loyaltyEnabled && v && v.available && v.id === raw.loyaltyIntroVariantId &&
      v.allocations.length && !oneTimeOnly);
  }

  function renderLoyalty() {
    var base = document.querySelector('[data-loyalty-base]');
    var standard = document.querySelector('[data-loyalty-standard]');
    /* One-time only (Dom 2026-10-02): subscribers are enrolled in the
       program outright, so the join pitch is noise on Subscribe & Save. */
    if (base) base.hidden = !currentVariant() || effectivePlan() !== 'one-time';
    if (standard) standard.hidden = !raw.loyaltyEnabled;
  }

  function renderPlans() {
    renderLoyalty();
    var v = currentVariant();
    if (!v) { planSection.innerHTML = ''; return; }

    var plan = effectivePlan();
    var subPrice = subPriceCents(v);

    // No subscribe option -> a lone One-time card is just noise between the
    // pack picker and the Reserve CTA. Hide the whole step (header included);
    // the CTA carries the price. Same treatment under the one-time-only
    // kill switch: there is nothing to choose, so no radios at all.
    var planStep = document.querySelector('[data-plan-step]');
    if (oneTimeOnly || (subPrice == null && !subscribeOnly)) {
      planSection.innerHTML = '';
      if (planStep) planStep.style.display = 'none';
      return;
    }
    if (planStep) planStep.style.display = '';

    // Subscribe & Save renders ABOVE One-time (Dom 2026-06-14) — leads with
    // the default/preferred plan.
    // Cadence-style offer card (Dom 2026-09-29): full-width red banner
    // across the card top instead of the corner tag, a save-amount pill
    // next to the label, and a summary line above the months strip.
    var loyaltyMix = starterOfferAvailable() && state.tier === 'mix';
    var loyaltyCopy = raw.loyaltyOffer || {};
    var subHtml = '';
    if (subPrice != null) {
      var pct = Math.round((1 - subPrice / v.price) * 100);
      /* 3/5-packs share the mix card's dress (full-width banner, offer type
         scale) and the same discount structure (pct off the first order,
         then 15% in perpetuity), minus the loyalty extras (Dom 2026-10-01).
         subPrice is the first-order allocation, so pct is the first-order
         discount. */
      var subBannerText = null;
      if (loyaltyMix && loyaltyCopy.planBanner) {
        subBannerText = loyaltyCopy.planBanner;
      } else if (pct > 0) {
        subBannerText = pct + '% off first order';
      }
      subHtml += '<button type="button" aria-pressed="' + (plan === 'subscribe') + '" class="bxb__plan bxb__plan--offer' + (plan === 'subscribe' ? ' is-active' : '') + '" data-plan="subscribe">';
      if (subBannerText) {
        subHtml += '<span class="bxb__plan-banner">' + escapeHtml(subBannerText) + '</span>';
      }
      subHtml += '  <span class="bxb__plan-top">';
      subHtml += '    <span class="bxb__radio"></span>';
      subHtml += '    <span class="bxb__plan-namewrap">';
      subHtml += '      <span class="bxb__plan-name">Subscribe &amp; Save</span>';
      if (v.price > subPrice) {
        subHtml += '    <span class="bxb__plan-save">- Save ' + fmt(v.price - subPrice) + '</span>';
      }
      subHtml += '    </span>';
      subHtml += '    <span class="bxb__plan-pricecol">';
      subHtml += '      <span class="bxb__plan-price">' + fmt(subPrice) + '</span>';
      subHtml += '      <span class="bxb__plan-was">' + fmt(v.price) + '</span>';
      subHtml += '    </span>';
      subHtml += '  </span>';
      if (plan === 'subscribe' && loyaltyMix && loyaltyCopy.planSummaryHtml) {
        subHtml += '<span class="bxb__plan-summary">' + loyaltyCopy.planSummaryHtml + '</span>';
      }
      // Mix: the Month 1/2/3 strip's imagery carries the intro offer (no
      // perk check rows; skip/swap/cancel lives in the frequency line).
      // 3/5-packs: the classic card — settings perks list + corner tag.
      if (plan === 'subscribe' && !loyaltyMix) {
        // The card states the discount itself (banner: pct off first order,
        // lead perk: the 15% forever), so settings perks claiming a %-off
        // would contradict it and drop; the rest render as before.
        var perksShown = subPerks.filter(function (p) { return !/%\s*off/i.test(p); });
        if (subBannerText) perksShown.unshift('15% off all future orders');
        if (perksShown.length > 0) {
          subHtml += '<span class="bxb__plan-perks">';
          perksShown.forEach(function (p) {
            subHtml += '<span class="bxb__plan-perk">' + CHECK_SVG + escapeHtml(p) + '</span>';
          });
          subHtml += '</span>';
        }
      }
      if (plan === 'subscribe' && loyaltyMix && loyaltyPlanHtml) {
        subHtml += '<span class="bxb__plan-deliveries">' + loyaltyPlanHtml + '</span>';
      }
      if (plan === 'subscribe') {
        // Frequency lives with the subscription details, not the CTA
        // (Sam's copy pass, 2026-08-16; replaces the old refills note).
        // 3/5-packs say skip/swap/cancel in their perks checklist, so their
        // line is cadence only; the mix card has no checklist, so it keeps
        // the promise here (Dom 2026-10-03).
        subHtml += '<span class="bxb__plan-freq">'
          + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="14" height="14"><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15"></path></svg>'
          + 'Ships ' + escapeHtml(v.allocations[0].sellingPlanName.toLowerCase())
          + (loyaltyMix ? '. Skip, swap or cancel anytime.' : '.') + '</span>';
      }
      subHtml += '</button>';

    }

    // One-time nets the preorder discount, and the card says so — otherwise
    // it reads full price next to the pack card's discounted number.
    var oneHtml = '';
    oneHtml += '<button type="button" aria-pressed="' + (plan === 'one-time') + '" class="bxb__plan' + (plan === 'one-time' ? ' is-active' : '') + '" data-plan="one-time">';
    if (reserveDiscountPct > 0) {
      oneHtml += '<span class="bxb__plan-tag bxb__plan-tag--reserve">Early access &middot; ' + reserveDiscountPct + '% off</span>';
    }
    oneHtml += '  <span class="bxb__plan-top">';
    oneHtml += '    <span class="bxb__radio"></span>';
    oneHtml += '    <span class="bxb__plan-name">One-time</span>';
    oneHtml += '    <span class="bxb__plan-pricecol">';
    oneHtml += '      <span class="bxb__plan-price">' + fmt(reserveOff(v.price)) + '</span>';
    if (reserveDiscountPct > 0) {
      oneHtml += '      <span class="bxb__plan-was">' + fmt(v.price) + '</span>';
    }
    oneHtml += '    </span>';
    oneHtml += '  </span>';
    oneHtml += '</button>';

    planSection.innerHTML = subHtml + (subscribeOnly ? '' : oneHtml);

    planSection.querySelectorAll('[data-plan]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.planChoice = btn.getAttribute('data-plan');
        renderPlans();
        renderCta();
        var selectedButton = planSection.querySelector('[data-plan="' + state.planChoice + '"]');
        if (selectedButton) selectedButton.focus({ preventScroll: true });
      });
    });
  }

  function renderCta() {
    var v = currentVariant();
    var subscribed = effectivePlan() === 'subscribe';
    if (v && v.available) {
      buyBtn.disabled = cartPending;
      buyBtn.childNodes[0].textContent = ctaText + ' ';
      var showWas = !subscribed && reserveDiscountPct > 0 && currentPriceCents() < v.price;
      ctaPriceEl.innerHTML = '&mdash; ' + fmt(currentPriceCents())
        + (showWas ? ' <s class="bxb__cta-was">' + fmt(v.price) + '</s>' : '');
    } else {
      buyBtn.disabled = true;
      buyBtn.childNodes[0].textContent = v ? 'Sold Out' : 'Unavailable';
      ctaPriceEl.innerHTML = '';
    }
    renderSticky();
  }

  /* ------------------------------------------------------------------ */
  /* Sticky bar                                                          */
  /* ------------------------------------------------------------------ */

  function renderSticky() {
    if (!stickyBar) return;
    var v = currentVariant();
    var subscribed = effectivePlan() === 'subscribe';
    stickyConfig.textContent = configLabel();
    // With the plan step killed there is no choice to echo back.
    stickyPlan.textContent = oneTimeOnly ? '' : (subscribed ? 'Subscribe & Save' : 'One-time');
    stickyPrice.textContent = v ? fmt(currentPriceCents()) : '';
    stickyBuy.disabled = cartPending || !(v && v.available);

    /* Switcher. The row used to be hidden whenever the sampler was active,
       which made the sampler a one-way door: from a single flavour there was
       no sampler option, and from the sampler there was no way back to a
       flavour without scrolling up to the buy box. The bundles are now always
       the first options here (Dom 2026-09-02), in buy-box row order:
       mix, then the founder sampler. */
    stickyDots.style.display = '';
    var html = '<span class="bxb-sticky__dots-label">Pick</span>';

    /* Bundle dot face: the product's own thumb when it has one — with two
       bundle dots side by side, two identical flavor-wheel swatches would be
       indistinguishable. The conic quadrant built from the live flavour set
       stays as the no-image fallback. */
    function bundleDotHtml(tierId, pack, name, sub) {
      if (!TIERS[tierId]) return '';
      var active = state.tier === tierId;
      var first = pack && pack.images && pack.images[0];
      var face;
      if (first && (first.thumb || first.src)) {
        face = '<span style="background: url(' + (first.thumb || first.src) + ') center/cover;"></span>';
      } else {
        var slice = 100 / flavors.length;
        var stops = flavors.map(function (flavor, i) {
          var c = (FLAVOR_CONFIG[flavor] || {}).color || '#ccc';
          return c + ' ' + (slice * i) + '% ' + (slice * (i + 1)) + '%';
        }).join(', ');
        face = '<span style="background: conic-gradient(' + stops + ');"></span>';
      }
      return '<button type="button" class="bxb-sticky__dot bxb-sticky__dot--sampler'
           + (active ? ' is-active' : '') + '" data-sticky-bundle="' + tierId + '"'
           + ' title="' + escapeHtml(name) + ', ' + escapeHtml(sub) + '"'
           + ' aria-label="' + escapeHtml(name) + ', ' + escapeHtml(sub) + '"'
           + ' aria-pressed="' + (active ? 'true' : 'false') + '">'
           + face + '</button>';
    }

    html += bundleDotHtml('mix', mixPack, MIX_NAME, MIX_SUB);
    html += bundleDotHtml('sampler', fourPack, SAMPLER_NAME, SAMPLER_SUB);

    flavors.forEach(function (flavor) {
      var cfg = FLAVOR_CONFIG[flavor] || { color: '#ccc' };
      var soldOut = !flavorAvailable(flavor);
      var active = !isBundle() && state.flavor === flavor;
      html += '<button type="button" class="bxb-sticky__dot' + (active ? ' is-active' : '') + '"'
            + ' data-sticky-flavor="' + escapeHtml(flavor) + '"' + (soldOut ? ' disabled' : '')
            + ' title="' + escapeHtml(flavor) + (soldOut ? ' — sold out' : '') + '"'
            + ' aria-pressed="' + (active ? 'true' : 'false') + '">'
            + '<span style="background:' + cfg.color + ';"></span></button>';
    });

    stickyDots.innerHTML = html;
    stickyDots.querySelectorAll('[data-sticky-flavor]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        pickFlavor(btn.getAttribute('data-sticky-flavor'));
      });
    });
    stickyDots.querySelectorAll('[data-sticky-bundle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tierId = btn.getAttribute('data-sticky-bundle');
        if (state.tier !== tierId) pickFlavor(tierId);
      });
    });
  }

  var stickyVisible = false;

  function updateStickyVisibility() {
    if (!stickyBar || !ctaAnchor) return;
    var drawerOpen = document.querySelector('.cart-drawer.is-open');
    var passed = ctaAnchor.getBoundingClientRect().bottom < 70;
    var show = passed && !drawerOpen;
    if (show !== stickyVisible) {
      stickyVisible = show;
      stickyBar.classList.toggle('is-visible', show);
      stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
  }

  window.addEventListener('scroll', updateStickyVisibility, { passive: true });

  /* ------------------------------------------------------------------ */
  /* Gallery (same resolution rules as the April PDP)                    */
  /* ------------------------------------------------------------------ */

  function filterImgs(arr) {
    return (arr || []).filter(function (img) { return img && img.src; });
  }
  var customGallery4Pack  = filterImgs(raw.customGallery4Pack);
  var customGallery5Pack  = filterImgs(raw.customGallery5Pack);
  var customGalleryFlavor = raw.customGalleryFlavor || {};
  /* 5-can tier can override the per-flavor hero, so the image matches the
     pack being bought. Falls back to customGalleryFlavor when unset. */
  var customGallery5PackFlavor = raw.customGallery5PackFlavor || {};
  /* Same for the 3-can tier, with one extra rung: an unset theme setting
     falls back to the 3-pack variant image from admin before landing on
     the shared flavor hero. */
  var customGallery3PackFlavor = raw.customGallery3PackFlavor || {};

  function getFilteredImages() {
    if (isSampler()) {
      if (customGallery4Pack.length > 0) return customGallery4Pack;
      return fourPack && (fourPack.images || []).length > 0 ? fourPack.images : (fivePack.images || []);
    }
    /* The mix bundle shows its own product photos — customGallery4Pack is
       founder-bundle art, wrong product to borrow. */
    if (state.tier === 'mix') {
      return mixPack && (mixPack.images || []).length > 0 ? mixPack.images : (fivePack.images || []);
    }

    var flavorImg = customGalleryFlavor[state.flavor];
    if (state.tier === 's1' && customGallery5PackFlavor[state.flavor]) {
      flavorImg = customGallery5PackFlavor[state.flavor];
    }
    if (state.tier === 'c3') {
      var v3 = TIERS.c3 && TIERS.c3.byFlavor[state.flavor];
      if (customGallery3PackFlavor[state.flavor]) {
        flavorImg = customGallery3PackFlavor[state.flavor];
      } else if (v3 && v3.image) {
        flavorImg = { src: v3.image, thumb: v3.image, alt: v3.imageAlt || v3.title };
      }
    }
    if (flavorImg || customGallery5Pack.length > 0) {
      var combined = [];
      if (flavorImg && flavorImg.src) combined.push(flavorImg);
      customGallery5Pack.forEach(function (img) {
        if (!flavorImg || img.src !== flavorImg.src) combined.push(img);
      });
      if (combined.length > 0) return combined;
    }

    // Fallback: variant image + alt-text-flavor-matched + shared images.
    var v = TIERS.s1.byFlavor[state.flavor];
    var flavorLower = state.flavor.toLowerCase();
    var productImages = fivePack.images || [];
    var variantImageUrls = {};
    fivePack.variants.forEach(function (variant) {
      if (variant.image) variantImageUrls[variant.image] = true;
    });
    var flavorNames = flavors.map(function (f) { return f.toLowerCase(); });
    var result = [];
    if (v && v.image) result.push({ src: v.image, thumb: v.image, alt: v.imageAlt || v.title });
    productImages.forEach(function (img) {
      if (variantImageUrls[img.src]) return;
      var alt = (img.alt || '').toLowerCase();
      var mentionsFlavor = flavorNames.some(function (f) { return alt.indexOf(f) !== -1; });
      if (!mentionsFlavor || alt.indexOf(flavorLower) !== -1) result.push(img);
    });
    return result.length > 0 ? result : productImages;
  }

  var lastGalleryKey = null;

  function updateGallery() {
    if (!galleryMain) return;
    var images = getFilteredImages();
    if (images.length === 0) return;

    // Same image set as last render (e.g. pack-size change within a
    // flavor) — leave the DOM alone so nothing flashes.
    var galleryKey = images.map(function (img) { return img.src; }).join('|');
    if (galleryKey === lastGalleryKey) return;
    lastGalleryKey = galleryKey;

    galleryMain.removeAttribute('srcset');
    galleryMain.removeAttribute('sizes');
    galleryMain.src = images[0].src;
    galleryMain.alt = images[0].alt || 'ZALT Electrolyte Pouches';

    if (images.length > 1) {
      var html = '';
      images.forEach(function (img, i) {
        html += '<button type="button" class="product-gallery__thumb' + (i === 0 ? ' is-active' : '') + '" data-thumb-idx="' + i + '">'
              + '<img src="' + (img.thumb || img.src) + '" alt="' + escapeHtml(img.alt || 'View ' + (i + 1)) + '" width="64" height="64" loading="lazy">'
              + '</button>';
      });
      galleryThumbs.innerHTML = html;
      galleryThumbs.style.display = '';
      galleryThumbs.querySelectorAll('[data-thumb-idx]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = parseInt(btn.getAttribute('data-thumb-idx'), 10);
          galleryMain.src = images[idx].src;
          galleryMain.alt = images[idx].alt || '';
          galleryThumbs.querySelectorAll('.product-gallery__thumb').forEach(function (t, ti) {
            t.classList.toggle('is-active', ti === idx);
          });
        });
      });
    } else {
      galleryThumbs.innerHTML = '';
      galleryThumbs.style.display = 'none';
    }
  }

  /* ------------------------------------------------------------------ */
  /* State transitions                                                   */
  /* ------------------------------------------------------------------ */

  function pickFlavor(flavor) {
    if (flavor === 'sampler' || flavor === 'mix') {
      state.flavor = flavor;
      state.tier = flavor;
    } else {
      state.flavor = flavor;
      // Coming from a bundle → the default single-flavor tier; otherwise
      // keep the tier the visitor already chose (3 or 5 cans).
      if (state.tier === 'sampler' || state.tier === 'mix') state.tier = DEFAULT_SINGLE_TIER;
      // Tier may not be buyable for this flavor (e.g. 3-pack flavor OOS) —
      // fall over to the 5-can sleeve.
      var t = TIERS[state.tier];
      if (!t.byFlavor[flavor] || !t.byFlavor[flavor].available) {
        state.tier = 's1';
      }
    }
    renderAll();
    document.dispatchEvent(new CustomEvent('pdp:flavor-changed', {
      // Both bundles are multi-flavor: the page accents fall back to the
      // mixed-fruit set (flavor-accents.js treats 'Variety Pack' as neutral).
      detail: { flavor: flavor === 'sampler' || flavor === 'mix' ? 'Variety Pack' : flavor }
    }));
    fireViewItem();
  }

  function renderAll() {
    renderFlavors();
    renderPacks();
    renderPlans();
    renderCta();
    updateGallery();
  }

  if (samplerBtn) {
    samplerBtn.addEventListener('click', function () {
      if (state.flavor !== 'sampler') pickFlavor('sampler');
    });
  }
  if (mixBtn) {
    mixBtn.addEventListener('click', function () {
      if (state.flavor !== 'mix') pickFlavor('mix');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                         */
  /* ------------------------------------------------------------------ */

  function getArm() {
    return (window.ZaltAB && typeof window.ZaltAB.getArm === 'function')
      ? window.ZaltAB.getArm()
      : 'drawer';
  }

  function trackEvent(name, data) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data);
    } else if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: name }, data));
    }
  }

  function analyticsItems(v, priceCents) {
    return [{
      item_id: v.productId,
      item_name: v.productTitle,
      item_variant: v.title,
      price: priceCents / 100,
      quantity: 1
    }];
  }

  function fireViewItem() {
    var v = currentVariant();
    if (!v) return;
    trackEvent('view_item', {
      pdp_arm: 'buybox',
      ecommerce: { currency: 'USD', value: v.price / 100, items: analyticsItems(v, v.price) }
    });
  }

  function showBuyError(msg, errorEl) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
    errorEl.style.display = '';
  }

  function addToCart(trigger) {
    if (cartPending) return;
    var v = currentVariant();
    if (!v || !v.available) return;

    // Snapshot the selected purchase before awaiting Skio or the cart API.
    var subscribed = effectivePlan() === 'subscribe';
    var sellingPlan = subscribed && v.allocations.length > 0
      ? parseInt(v.allocations[0].sellingPlanId, 10)
      : null;
    var priceCents = subscribed ? subPriceCents(v) : reserveOff(v.price);
    var errorEl = buyErrorEl;
    cartPending = true;
    renderCta();
    var restoreLabel = function () {
      cartPending = false;
      renderCta();
    };
    if (errorEl) { errorEl.hidden = true; errorEl.style.display = 'none'; }

    trackEvent('add_to_cart', {
      arm: getArm(),
      pdp_arm: 'buybox',
      purchase_source: 'regular-pdp',
      ecommerce: { currency: 'USD', value: priceCents / 100, items: analyticsItems(v, priceCents) }
    });

    function buildBody(dynamicBoxId) {
      var body = { id: v.id, quantity: 1 };
      if (sellingPlan) {
        body.selling_plan = sellingPlan;
        if (dynamicBoxId) {
          body.properties = { _dynamicBoxId: dynamicBoxId, _dynamicBoxIndex: 0 };
        }
      }
      return body;
    }

    var bodyPromise = sellingPlan && skioBoxIdPromise
      ? skioBoxIdPromise.then(buildBody)
      : Promise.resolve(buildBody(null));

    bodyPromise
      .then(function (body) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      })
      .then(function (r) {
        return r.json().then(function (json) { return { ok: r.ok, item: json }; });
      })
      .then(function (res) {
        if (!res.ok) {
          showBuyError((res.item && res.item.description) || 'Couldn’t add to cart. Please try again.', errorEl);
          restoreLabel();
          return;
        }
        if (window.ZaltCart && typeof window.ZaltCart.openAfterAdd === 'function') {
          window.ZaltCart.openAfterAdd(res.item, { checkoutUrl: '/checkout', trigger: trigger || buyBtn });
        } else {
          window.location.href = '/checkout';
        }
        restoreLabel();
      })
      .catch(function () {
        showBuyError('Couldn’t add to cart. Please check your connection and try again.', errorEl);
        restoreLabel();
      });
  }

  buyBtn.addEventListener('click', function () { addToCart(buyBtn); });
  if (stickyBuy) stickyBuy.addEventListener('click', function () { addToCart(stickyBuy); });

  /* ------------------------------------------------------------------ */
  /* Info tabs                                                           */
  /* ------------------------------------------------------------------ */


  /* ------------------------------------------------------------------ */
  /* URL params (?variant=<id>&flavor=cool-mint                         */
  /*             &pack=3|5|sampler|founder|mix&plan=sub|onetime)         */
  /* ------------------------------------------------------------------ */

  (function applyUrlParams() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }

    function normalize(s) {
      return String(s || '').toLowerCase().replace(/[\s_+-]+/g, ' ').trim();
    }

    /* ?variant=<numeric id> is the machine-generated deep link: Google and
       Shop product feeds land on /products/<handle>?variant=... . Resolved
       across all three tier products; the hand-written ?flavor/?pack params
       below still win when both are present. Iterating `flavors` (not the
       byFlavor keys) keeps hidden flavors unselectable, same as ?flavor. */
    var variantParam = parseInt(params.get('variant') || '', 10);
    if (variantParam) {
      if (TIERS.sampler && TIERS.sampler.variant.id === variantParam) {
        state.flavor = 'sampler';
        state.tier = 'sampler';
      } else if (TIERS.mix && TIERS.mix.variant.id === variantParam) {
        state.flavor = 'mix';
        state.tier = 'mix';
      } else {
        ['s1', 'c3'].forEach(function (tierId) {
          var t = TIERS[tierId];
          if (!t || !t.byFlavor) return;
          flavors.forEach(function (f) {
            var entry = t.byFlavor[f];
            if (!entry || entry.id !== variantParam || !flavorAvailable(f)) return;
            state.flavor = f;
            /* Exact tier when that variant is buyable; the flavor at the
               default tier when only the other pack size has stock. */
            state.tier = entry.available ? tierId : DEFAULT_SINGLE_TIER;
          });
        });
      }
    }

    var flavorParam = params.get('flavor');
    if (flavorParam) {
      var wanted = normalize(flavorParam);
      var match = flavors.filter(function (f) { return normalize(f) === wanted; })[0];
      if (match && flavorAvailable(match)) {
        state.flavor = match;
        state.tier = DEFAULT_SINGLE_TIER;
      }
    }

    var packParam = normalize(params.get('pack') || params.get('bundle'));
    /* 3/5-can pack params only make sense on a single flavor — on either
       bundle (including the mix default) they would force a tier with no
       byFlavor entry for the current selection. */
    var onBundle = state.flavor === 'sampler' || state.flavor === 'mix';
    if (packParam === '3' || packParam === '3 pack' || packParam === '3pack') {
      if (TIERS.c3 && !onBundle) state.tier = 'c3';
    } else if (packParam === '5' || packParam === '5 pack' || packParam === '5pack') {
      if (!onBundle) state.tier = 's1';
    } else if (packParam === '4' || packParam === '4 pack' || packParam === '4pack' || packParam === 'sampler' || packParam === 'founder') {
      /* Founder links keep working when the founder tile is off: they land
         on whichever 4-can bundle is actually offered. */
      var bundleTier = TIERS.sampler ? 'sampler' : (TIERS.mix ? 'mix' : null);
      if (bundleTier) {
        state.flavor = bundleTier;
        state.tier = bundleTier;
      }
    } else if (TIERS.mix && (packParam === 'mix' || packParam === 'mixed' || packParam === 'salt mix')) {
      state.flavor = 'mix';
      state.tier = 'mix';
    }

    var planParam = normalize(params.get('plan'));
    if (planParam === 'sub' || planParam === 'subscribe') state.planChoice = 'subscribe';
    else if (!subscribeOnly && (planParam === 'onetime' || planParam === 'one time')) state.planChoice = 'one-time';
  })();

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  buildFlavorGrid();
  renderAll();
  updateStickyVisibility();
  setTimeout(fireViewItem, 100);
})();
